import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Volume2, VolumeX, Zap, Truck, Shield, Menu, X, ChevronRight, Star, Quote, Calculator, ChevronDown, HelpCircle, Send, MessageCircle, Music, Download, Printer, CheckCircle, Play, Pause } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LandingPage() {
  const [speakers, setSpeakers] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [quoteEquipment, setQuoteEquipment] = useState("");
  const [quoteDays, setQuoteDays] = useState("1");
  const [quoteQty, setQuoteQty] = useState("1");
  const [openFaq, setOpenFaq] = useState(null);
  const [contactForm, setContactForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sendingContact, setSendingContact] = useState(false);
  const [musicPacks, setMusicPacks] = useState([]);
  const [buyingPack, setBuyingPack] = useState(null);
  const [packForm, setPackForm] = useState({ name: "", email: "", discount_code: "" });
  const [packSubmitting, setPackSubmitting] = useState(false);
  const [packPurchased, setPackPurchased] = useState(false);
  const [packDiscount, setPackDiscount] = useState(null);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewForm, setReviewForm] = useState({ customer_name: "", rating: 5, comment: "", event_type: "" });
  const [submittingReview, setSubmittingReview] = useState(false);
  const [djForm, setDjForm] = useState({ name: "", email: "", phone: "", event_date: "", event_type: "", venue: "", hours: "3", start_time: "", end_time: "", message: "" });
  const [djSubmitting, setDjSubmitting] = useState(false);
  const [djSubmitted, setDjSubmitted] = useState(false);
  const [djContractAgreed, setDjContractAgreed] = useState(false);

  // Music player
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const toggleMusic = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [speakersRes, reviewsRes, packsRes] = await Promise.all([
        axios.get(`${API}/speakers`),
        axios.get(`${API}/reviews`),
        axios.get(`${API}/music-packs`)
      ]);
      setSpeakers(speakersRes.data);
      setReviews(reviewsRes.data);
      setMusicPacks(packsRes.data.filter(p => p.available));
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!contactForm.name || !contactForm.email || !contactForm.message) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSendingContact(true);
    try {
      await axios.post(`${API}/contact`, contactForm);
      toast.success("Message sent! We'll get back to you soon.");
      setContactForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error("Failed to send message. Try WhatsApp instead!");
    } finally {
      setSendingContact(false);
    }
  };

  const handlePackPurchase = async (e) => {
    e.preventDefault();
    if (!packForm.name || !packForm.email) {
      toast.error("Please fill in your name and email");
      return;
    }
    setPackSubmitting(true);
    try {
      const res = await axios.post(`${API}/music-orders`, {
        pack_id: buyingPack.id,
        customer_name: packForm.name,
        customer_email: packForm.email,
        discount_code: packForm.discount_code || undefined
      });
      if (res.data.discount_applied) {
        setPackDiscount(res.data.discount_applied);
      }
      setPackPurchased(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit order");
    } finally {
      setPackSubmitting(false);
    }
  };

  const handleStripePackPurchase = async () => {
    if (!packForm.name || !packForm.email) {
      toast.error("Please fill in your name and email first");
      return;
    }
    setPackSubmitting(true);
    try {
      // Create order first
      await axios.post(`${API}/music-orders`, {
        pack_id: buyingPack.id,
        customer_name: packForm.name,
        customer_email: packForm.email,
        discount_code: packForm.discount_code || undefined
      });
      // Create Stripe checkout
      const res = await axios.post(`${API}/payments/create-checkout`, {
        payment_type: "music_pack",
        item_id: buyingPack.id,
        origin_url: window.location.origin,
        discount_code: packForm.discount_code || undefined
      });
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to initiate payment");
      setPackSubmitting(false);
    }
  };

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    if (!reviewForm.customer_name || !reviewForm.comment) {
      toast.error("Please fill in your name and review");
      return;
    }
    setSubmittingReview(true);
    try {
      await axios.post(`${API}/reviews`, reviewForm);
      toast.success("Review posted! Thank you!");
      setReviewForm({ customer_name: "", rating: 5, comment: "", event_type: "" });
      setShowReviewForm(false);
      const res = await axios.get(`${API}/reviews`);
      setReviews(res.data);
    } catch {
      toast.error("Failed to submit review");
    } finally {
      setSubmittingReview(false);
    }
  };

  const handleDjRequest = async (e) => {
    e.preventDefault();
    if (!djForm.name || !djForm.email || !djForm.phone || !djForm.event_date) {
      toast.error("Please fill in all required fields");
      return;
    }
    if (!djForm.start_time || !djForm.end_time) {
      toast.error("Please select a start and end time");
      return;
    }
    if (!djContractAgreed) {
      toast.error("Please agree to the DJ booking contract");
      return;
    }
    setDjSubmitting(true);
    try {
      await axios.post(`${API}/dj-bookings`, {
        customer_name: djForm.name,
        customer_email: djForm.email,
        customer_phone: djForm.phone,
        event_date: djForm.event_date,
        event_type: djForm.event_type,
        venue: djForm.venue,
        hours: `${djForm.start_time} - ${djForm.end_time}`,
        message: djForm.message,
        contract_signed: djContractAgreed,
        contract_name: djForm.name,
        contract_address: djForm.venue || "",
        contract_date: new Date().toLocaleDateString('en-GB')
      });
      setDjSubmitted(true);
      toast.success("DJ request sent! We'll be in touch shortly.");
    } catch {
      toast.error("Failed to send request. Please try WhatsApp instead.");
    } finally {
      setDjSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#141414] noise-bg">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass-panel">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="https://static.prod-images.emergentagent.com/jobs/641f6878-5cef-40eb-8d31-2194fc50afac/images/9204c88f36c8aba1418f399ed70957b9e9b435b9671cfdca88f8cc5e5e0a2467.png" alt="The Humble Hires" className="h-40" data-testid="logo" />
          </div>
          
          <div className="hidden md:flex items-center gap-8">
            <a href="#speakers" className="text-sm uppercase tracking-wider text-white/70 hover:text-[#FF0033] transition-colors">
              Equipment
            </a>
            <a href="#bundles" className="text-sm uppercase tracking-wider text-white/70 hover:text-[#FF0033] transition-colors">
              Bundles
            </a>
            <button onClick={() => navigate("/build-bundle")} className="text-sm uppercase tracking-wider text-white/70 hover:text-[#FF0033] transition-colors">
              Build Order
            </button>
            <a href="#quote" className="text-sm uppercase tracking-wider text-white/70 hover:text-[#FF0033] transition-colors">
              Get Quote
            </a>
            <a href="#bookdj" className="text-sm uppercase tracking-wider text-white/70 hover:text-[#FF0033] transition-colors">
              Book a DJ
            </a>
            <a href="#music" className="text-sm uppercase tracking-wider text-white/70 hover:text-[#FF0033] transition-colors">
              DJ Packs
            </a>
            <a href="#videos" className="text-sm uppercase tracking-wider text-white/70 hover:text-[#FF0033] transition-colors">
              Videos
            </a>
            <a href="#instagram" className="text-sm uppercase tracking-wider text-white/70 hover:text-[#FF0033] transition-colors">
              Instagram
            </a>
            <a href="#mixes" className="text-sm uppercase tracking-wider text-white/70 hover:text-[#FF0033] transition-colors">
              Mixes
            </a>
            <a href="#reviews" className="text-sm uppercase tracking-wider text-white/70 hover:text-[#FFD700] transition-colors">
              Reviews
            </a>
            <a href="#faq" className="text-sm uppercase tracking-wider text-white/70 hover:text-[#FF0033] transition-colors">
              FAQ
            </a>
            <a href="#contact" className="text-sm uppercase tracking-wider text-white/70 hover:text-[#FF0033] transition-colors">
              Contact
            </a>
            <a href="#features" className="text-sm uppercase tracking-wider text-white/70 hover:text-[#FF0033] transition-colors">
              Why Us
            </a>
            <Button 
              onClick={() => navigate('/admin')}
              variant="ghost"
              className="text-sm uppercase tracking-wider text-white/70 hover:text-[#FF0033] hover:bg-transparent"
              data-testid="admin-link"
            >
              Admin
            </Button>
          </div>

          <button 
            className="md:hidden text-white"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-40 bg-[#141414]/98 backdrop-blur-xl flex flex-col items-center justify-center gap-8">
          <a 
            href="#speakers" 
            onClick={() => setMobileMenuOpen(false)}
            className="text-2xl uppercase tracking-wider text-white/80 hover:text-[#FF0033]"
          >
            Equipment
          </a>
          <a 
            href="#bundles" 
            onClick={() => setMobileMenuOpen(false)}
            className="text-2xl uppercase tracking-wider text-white/80 hover:text-[#FF0033]"
          >
            Bundles
          </a>
          <button 
            onClick={() => { setMobileMenuOpen(false); navigate("/build-bundle"); }}
            className="text-2xl uppercase tracking-wider text-white/80 hover:text-[#FF0033]"
          >
            Build Order
          </button>
          <a 
            href="#quote" 
            onClick={() => setMobileMenuOpen(false)}
            className="text-2xl uppercase tracking-wider text-white/80 hover:text-[#FF0033]"
          >
            Get Quote
          </a>
          <a 
            href="#bookdj" 
            onClick={() => setMobileMenuOpen(false)}
            className="text-2xl uppercase tracking-wider text-white/80 hover:text-[#FF0033]"
          >
            Book a DJ
          </a>
          <a 
            href="#music" 
            onClick={() => setMobileMenuOpen(false)}
            className="text-2xl uppercase tracking-wider text-white/80 hover:text-[#FF0033]"
          >
            DJ Packs
          </a>
          <a 
            href="#videos" 
            onClick={() => setMobileMenuOpen(false)}
            className="text-2xl uppercase tracking-wider text-white/80 hover:text-[#FF0033]"
          >
            Videos
          </a>
          <a 
            href="#instagram" 
            onClick={() => setMobileMenuOpen(false)}
            className="text-2xl uppercase tracking-wider text-white/80 hover:text-[#FF0033]"
          >
            Instagram
          </a>
          <a 
            href="#mixes" 
            onClick={() => setMobileMenuOpen(false)}
            className="text-2xl uppercase tracking-wider text-white/80 hover:text-[#FF0033]"
          >
            Mixes
          </a>
          <a 
            href="#reviews" 
            onClick={() => setMobileMenuOpen(false)}
            className="text-2xl uppercase tracking-wider text-white/80 hover:text-[#FFD700]"
          >
            Reviews
          </a>
          <a 
            href="#faq" 
            onClick={() => setMobileMenuOpen(false)}
            className="text-2xl uppercase tracking-wider text-white/80 hover:text-[#FF0033]"
          >
            FAQ
          </a>
          <a 
            href="#contact" 
            onClick={() => setMobileMenuOpen(false)}
            className="text-2xl uppercase tracking-wider text-white/80 hover:text-[#FF0033]"
          >
            Contact
          </a>
          <a 
            href="#features" 
            onClick={() => setMobileMenuOpen(false)}
            className="text-2xl uppercase tracking-wider text-white/80 hover:text-[#FF0033]"
          >
            Why Us
          </a>
          <button 
            onClick={() => { navigate('/admin'); setMobileMenuOpen(false); }}
            className="text-2xl uppercase tracking-wider text-white/80 hover:text-[#FF0033]"
          >
            Admin
          </button>
        </div>
      )}

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center hero-gradient overflow-hidden">
        <div className="bg-lines" />
        <div className="absolute inset-0 glow-overlay" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 text-center">
          <p className="text-xs md:text-sm uppercase tracking-[0.3em] text-[#FF0033] mb-6 fade-in" data-testid="hero-subtitle">
            Premium Speaker Rental
          </p>
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-black tracking-tighter uppercase mb-8 fade-in fade-in-delay-1" data-testid="hero-title">
            Rent the Sound.<br />
            <span className="text-[#FF0033] neon-text">Own the Night.</span>
          </h1>
          <p className="text-lg md:text-xl text-white/70 max-w-2xl mx-auto mb-12 fade-in fade-in-delay-2">
            Professional RCF speakers for events of any size. Crystal clear audio, 
            powerful bass, delivered to your door.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center fade-in fade-in-delay-3">
            <Button 
              onClick={() => document.getElementById('speakers').scrollIntoView({ behavior: 'smooth' })}
              className="btn-primary px-8 py-6 text-base pulse-neon"
              data-testid="cta-browse-speakers"
            >
              Browse Speakers
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>

        {/* Hero background slideshow */}
        <div className={`absolute inset-0 z-0 ${isPlaying ? 'hero-bass-active' : ''}`}>
          <div 
            className="absolute inset-0 hero-slide-1"
            style={{
              backgroundImage: `url(https://static.prod-images.emergentagent.com/jobs/641f6878-5cef-40eb-8d31-2194fc50afac/images/75c006d4127b61a05759a851b11a35bf973134c992de97aa5cde4efb7dc03d07.png)`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              opacity: 0.6
            }}
          />
          <div 
            className="absolute inset-0 hero-slide-2"
            style={{
              backgroundImage: `url(https://static.prod-images.emergentagent.com/jobs/641f6878-5cef-40eb-8d31-2194fc50afac/images/b416e51a22344e83f482aa6add34076de6cb3b8aa5df054d35e8291d7c1f498a.png)`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              opacity: 0.6
            }}
          />
          <div 
            className="absolute inset-0 hero-slide-3"
            style={{
              backgroundImage: `url(https://static.prod-images.emergentagent.com/jobs/641f6878-5cef-40eb-8d31-2194fc50afac/images/ac7015d58f7c681349d9eacb60a77b70356c6399a02d7b42ff87a8f01becf634.png)`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              opacity: 0.6
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#141414] via-transparent to-[#141414]/60" />
        </div>
      </section>

      {/* Speakers Catalog */}
      <section id="speakers" className="py-24 md:py-32 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="mb-16">
            <p className="text-xs uppercase tracking-[0.2em] text-[#FF0033] mb-4">Our Fleet</p>
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight" data-testid="speakers-section-title">
              Individual Equipment
            </h2>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="neon-card h-[500px] animate-pulse">
                  <div className="h-64 bg-white/5" />
                  <div className="p-6 space-y-4">
                    <div className="h-6 bg-white/5 rounded w-3/4" />
                    <div className="h-4 bg-white/5 rounded w-full" />
                    <div className="h-4 bg-white/5 rounded w-2/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {speakers.filter(s => !s.is_bundle).map((speaker) => (
                <div 
                  key={speaker.id} 
                  className="neon-card speaker-card overflow-hidden group"
                  data-testid={`speaker-card-${speaker.id}`}
                >
                  <div className="relative h-64 overflow-hidden">
                    <img 
                      src={speaker.image_url} 
                      alt={speaker.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A] to-transparent" />
                    <div className="absolute top-4 right-4">
                      <span className={`px-3 py-1 text-xs uppercase tracking-wider ${speaker.available ? 'bg-[#FF0033]/20 text-[#FF0033]' : 'bg-[#FF0055]/20 text-[#FF0055]'}`}>
                        {speaker.available ? 'Available' : 'Unavailable'}
                      </span>
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <p className="text-xs uppercase tracking-wider text-[#FF0033] mb-2 mono">{speaker.model}</p>
                    <h3 className="text-xl font-bold mb-3">{speaker.name}</h3>
                    <p className="text-white/70 text-sm mb-4 line-clamp-2">{speaker.description}</p>
                    
                    <div className="flex flex-wrap gap-2 mb-6">
                      <span className="spec-tag">{speaker.specs.power}</span>
                      <span className="spec-tag">{speaker.specs.size}</span>
                      <span className="spec-tag">{speaker.specs.max_spl}</span>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-2xl font-bold text-[#FF0033]">£{speaker.price_per_day}</span>
                        <span className="text-white/50 text-sm">/day</span>
                      </div>
                      <Button 
                        onClick={() => navigate(`/build-bundle?add=${speaker.id}`)}
                        disabled={!speaker.available}
                        className="btn-primary px-6 py-3"
                        data-testid={`book-speaker-${speaker.id}`}
                      >
                        Request
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Sound Bundles Section */}
      <section id="bundles" className="py-24 md:py-32 px-6 bg-[#1A1A1A]">
        <div className="max-w-7xl mx-auto">
          <div className="mb-16">
            <p className="text-xs uppercase tracking-[0.2em] text-[#FF0033] mb-4">Save More</p>
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight" data-testid="bundles-section-title">
              Sound Bundles
            </h2>
            <p className="text-white/70 mt-4 max-w-2xl">
              Get more for less with our curated equipment bundles. Perfect packages for any event size.
            </p>
          </div>

          {!loading && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {speakers.filter(s => s.is_bundle === true).sort((a, b) => a.price_per_day - b.price_per_day).map((bundle) => (
                <div 
                  key={bundle.id} 
                  className="neon-card speaker-card overflow-hidden group border-[#FF0033]/20 hover:border-[#FF0033]/50"
                  data-testid={`bundle-card-${bundle.id}`}
                >
                  <div className="relative h-64 overflow-hidden">
                    <img 
                      src={bundle.image_url} 
                      alt={bundle.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A] to-transparent" />
                    <div className="absolute top-4 right-4">
                      <span className="px-3 py-1 text-xs uppercase tracking-wider bg-[#FF0033]/20 text-[#FF0033]">
                        Bundle
                      </span>
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <h3 className="text-xl font-bold mb-3">{bundle.name}</h3>
                    <p className="text-white/70 text-sm mb-4 line-clamp-2">{bundle.description}</p>
                    
                    <div className="flex flex-wrap gap-2 mb-6">
                      <span className="spec-tag bg-[#FF0033]/10 border-[#FF0033]/20">{bundle.specs?.includes}</span>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-2xl font-bold text-[#FF0033]">£{bundle.price_per_day}</span>
                        <span className="text-white/50 text-sm">/day</span>
                      </div>
                      <Button 
                        onClick={() => navigate(`/build-bundle?add=${bundle.id}`)}
                        disabled={!bundle.available}
                        className="bg-[#FF0033] text-white hover:bg-[#FF0033]/80 px-6 py-3 font-bold"
                        data-testid={`book-bundle-${bundle.id}`}
                      >
                        Request
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Book a DJ Section */}
      <section id="bookdj" className="py-24 md:py-32 px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#141414] via-[#1a0a10] to-[#141414]" />
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="relative rounded-xl overflow-hidden mb-6 border border-white/10">
                <img 
                  src="https://customer-assets.emergentagent.com/job_641f6878-5cef-40eb-8d31-2194fc50afac/artifacts/83vby2jy_IMG_3755.JPG" 
                  alt="DJ Kayz - The Humble Hires" 
                  className="w-full h-[400px] object-cover object-top"
                  data-testid="dj-photo"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#141414] via-transparent to-transparent" />
                <div className="absolute bottom-4 left-4">
                  <p className="text-[#FF0033] text-xs uppercase tracking-widest font-bold">Your DJ</p>
                  <p className="text-white text-xl font-bold">DJ Kayz (The Humble)</p>
                </div>
              </div>
              <p className="text-xs uppercase tracking-[0.2em] text-[#FF0033] mb-4">Live Entertainment</p>
              <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-6" data-testid="bookdj-title">
                Book a DJ
              </h2>
              <p className="text-white/70 text-lg mb-6 leading-relaxed">
                Need a DJ for your event? From birthdays and weddings to house parties and corporate events — 
                I'll bring the vibes, the equipment, and the energy to make your night unforgettable.
              </p>
              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-3 text-white/70">
                  <div className="w-8 h-8 rounded bg-[#FF0033]/10 flex items-center justify-center flex-shrink-0"><Music className="w-4 h-4 text-[#FF0033]" /></div>
                  <span>All genres covered — Afrobeats, R&B, Hip-Hop, Dancehall, House & more</span>
                </div>
                <div className="flex items-center gap-3 text-white/70">
                  <div className="w-8 h-8 rounded bg-[#FF0033]/10 flex items-center justify-center flex-shrink-0"><Volume2 className="w-4 h-4 text-[#FF0033]" /></div>
                  <span>Professional RCF sound system included</span>
                </div>
                <div className="flex items-center gap-3 text-white/70">
                  <div className="w-8 h-8 rounded bg-[#FF0033]/10 flex items-center justify-center flex-shrink-0"><Zap className="w-4 h-4 text-[#FF0033]" /></div>
                  <span>Full setup & breakdown — just enjoy the party</span>
                </div>
              </div>
            </div>

            <div className="glass-panel p-8 border-[#FF0033]/20" data-testid="dj-request-form">
              {djSubmitted ? (
                <div className="text-center py-8">
                  <CheckCircle className="w-16 h-16 text-[#39FF14] mx-auto mb-4" />
                  <h3 className="text-xl font-bold mb-2">Request Sent!</h3>
                  <p className="text-white/70 mb-4">We'll get back to you ASAP with availability and pricing.</p>
                  <Button onClick={() => { setDjSubmitted(false); setDjForm({ name: "", email: "", phone: "", event_date: "", event_type: "", venue: "", hours: "3", start_time: "", end_time: "", message: "" }); }} className="bg-white/10 text-white hover:bg-white/20">
                    Submit Another Request
                  </Button>
                </div>
              ) : (
                <>
                  <h3 className="text-lg font-bold mb-6 text-center">Request a DJ</h3>
                  <form onSubmit={handleDjRequest} className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs uppercase tracking-wider text-white/50 mb-1 block">Name *</label>
                        <Input value={djForm.name} onChange={e => setDjForm(f => ({ ...f, name: e.target.value }))} placeholder="Your name" className="bg-black/60 border-white/10 h-11" data-testid="dj-form-name" />
                      </div>
                      <div>
                        <label className="text-xs uppercase tracking-wider text-white/50 mb-1 block">Phone *</label>
                        <Input value={djForm.phone} onChange={e => setDjForm(f => ({ ...f, phone: e.target.value }))} placeholder="07xxx xxxxxx" className="bg-black/60 border-white/10 h-11" data-testid="dj-form-phone" />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-wider text-white/50 mb-1 block">Email *</label>
                      <Input type="email" value={djForm.email} onChange={e => setDjForm(f => ({ ...f, email: e.target.value }))} placeholder="your@email.com" className="bg-black/60 border-white/10 h-11" data-testid="dj-form-email" />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-wider text-white/50 mb-1 block">Event Date *</label>
                      <Input type="date" value={djForm.event_date} onChange={e => setDjForm(f => ({ ...f, event_date: e.target.value }))} className="bg-black/60 border-white/10 h-11" data-testid="dj-form-date" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs uppercase tracking-wider text-white/50 mb-1 block">Start Time *</label>
                        <Input type="time" value={djForm.start_time || ""} onChange={e => setDjForm(f => ({ ...f, start_time: e.target.value }))} className="bg-black/60 border-white/10 h-11" data-testid="dj-form-start-time" />
                      </div>
                      <div>
                        <label className="text-xs uppercase tracking-wider text-white/50 mb-1 block">End Time *</label>
                        <Input type="time" value={djForm.end_time || ""} onChange={e => setDjForm(f => ({ ...f, end_time: e.target.value }))} className="bg-black/60 border-white/10 h-11" data-testid="dj-form-end-time" />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-wider text-white/50 mb-1 block">Event Type</label>
                      <Input value={djForm.event_type} onChange={e => setDjForm(f => ({ ...f, event_type: e.target.value }))} placeholder="e.g. Birthday, Wedding, House Party" className="bg-black/60 border-white/10 h-11" data-testid="dj-form-event-type" />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-wider text-white/50 mb-1 block">Venue / Location</label>
                      <Input value={djForm.venue} onChange={e => setDjForm(f => ({ ...f, venue: e.target.value }))} placeholder="Venue name or address" className="bg-black/60 border-white/10 h-11" data-testid="dj-form-venue" />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-wider text-white/50 mb-1 block">Additional Info</label>
                      <Textarea value={djForm.message} onChange={e => setDjForm(f => ({ ...f, message: e.target.value }))} placeholder="Music preferences, theme, anything we should know..." rows={3} className="bg-black/60 border-white/10 resize-none" data-testid="dj-form-message" />
                    </div>

                    {/* DJ Contract */}
                    <div className="bg-black/40 border border-white/10 rounded-lg p-4 max-h-48 overflow-y-auto text-xs text-white/70 space-y-2">
                      <p className="font-bold text-white text-sm">DJ Booking Contract — The Humble Hires</p>
                      <p>By submitting this request, I agree to the following terms:</p>
                      <p><strong className="text-white">1. Cancellation by Client:</strong> If you cancel the booking, the deposit is non-refundable. No exceptions.</p>
                      <p><strong className="text-white">2. Cancellation by The Humble Hires:</strong> If we cancel the booking for any reason, you will receive a full refund of any payments made including the deposit.</p>
                      <p><strong className="text-white">3. Payment Terms:</strong> All payments must be made by the dates stated in your quote. Failure to pay on time may result in cancellation of the booking with no refund.</p>
                      <p><strong className="text-white">4. Deposit:</strong> A deposit (as stated in your quote) is required to secure your booking. The booking is not confirmed until the deposit is received.</p>
                      <p><strong className="text-white">5. Remaining Balance:</strong> If you selected a deposit option, the remaining balance must be paid as stated — either 24 hours before the event via bank transfer, or cash on arrival as agreed.</p>
                      <p><strong className="text-white">6. Event Changes:</strong> Any changes to the date, time, venue, or hours must be communicated at least 48 hours in advance. Changes are subject to availability.</p>
                      <p><strong className="text-white">7. Equipment:</strong> Professional sound equipment is included. Any damage caused to equipment during the event will be charged at full replacement cost.</p>
                      <p><strong className="text-white">8. Overtime:</strong> If the event runs beyond the agreed hours, additional time will be charged at a rate to be agreed beforehand.</p>
                    </div>

                    <label className="flex items-start gap-3 cursor-pointer" data-testid="dj-contract-label">
                      <input
                        type="checkbox"
                        checked={djContractAgreed}
                        onChange={e => setDjContractAgreed(e.target.checked)}
                        className="mt-1 w-5 h-5 accent-[#FF0033] rounded"
                        data-testid="dj-contract-checkbox"
                      />
                      <span className="text-xs text-white/70">I, <strong className="text-white">{djForm.name || '___'}</strong>, have read and agree to the DJ booking contract terms above.</span>
                    </label>

                    <Button type="submit" disabled={djSubmitting || !djContractAgreed} className="w-full bg-[#FF0033] text-white hover:bg-[#FF0033]/80 font-bold h-12 text-base disabled:opacity-40" data-testid="dj-form-submit">
                      {djSubmitting ? "Sending..." : "Send DJ Request"}
                    </Button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Instant Quote Calculator */}
      <section id="quote" className="py-24 md:py-32 px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#1A1A1A] via-[#1A1408] to-[#141414]" />
        <div className="max-w-4xl mx-auto relative z-10">
          <div className="text-center mb-12">
            <p className="text-xs uppercase tracking-[0.2em] text-[#FF0033] mb-4">Instant Pricing</p>
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight" data-testid="quote-section-title">
              Get a Quick Quote
            </h2>
            <p className="text-white/70 mt-4 max-w-xl mx-auto">
              Select your equipment and see the price instantly. No commitment needed.
            </p>
          </div>

          <div className="glass-panel p-8 md:p-10 border-[#FF0033]/20 hover:border-[#FF0033]/40 transition-colors" data-testid="quote-calculator">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              {/* Equipment Select */}
              <div>
                <label className="text-xs uppercase tracking-wider text-white/50 mb-2 block">Equipment</label>
                <Select value={quoteEquipment} onValueChange={setQuoteEquipment}>
                  <SelectTrigger className="bg-black/60 border-white/10 h-12 text-white" data-testid="quote-equipment-select">
                    <SelectValue placeholder="Choose equipment" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-white/10 max-h-64">
                    {speakers.map(s => (
                      <SelectItem key={s.id} value={s.id} className="hover:bg-[#FF0033]/10">
                        {s.name} — £{s.price_per_day}/day
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Duration */}
              <div>
                <label className="text-xs uppercase tracking-wider text-white/50 mb-2 block">Duration</label>
                <Select value={quoteDays} onValueChange={setQuoteDays}>
                  <SelectTrigger className="bg-black/60 border-white/10 h-12 text-white" data-testid="quote-days-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-white/10">
                    {[1, 2, 3, 4, 5, 6, 7].map(d => (
                      <SelectItem key={d} value={d.toString()} className="hover:bg-[#FF0033]/10">
                        {d} {d === 1 ? 'day' : 'days'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Quantity */}
              <div>
                <label className="text-xs uppercase tracking-wider text-white/50 mb-2 block">Quantity</label>
                <Select value={quoteQty} onValueChange={setQuoteQty}>
                  <SelectTrigger className="bg-black/60 border-white/10 h-12 text-white" data-testid="quote-qty-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-white/10">
                    {[1, 2, 3, 4].map(q => (
                      <SelectItem key={q} value={q.toString()} className="hover:bg-[#FF0033]/10">
                        {q} {q === 1 ? 'unit' : 'units'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Quote Result */}
            <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row items-center justify-between gap-6">
              <div>
                <p className="text-xs uppercase tracking-wider text-white/50 mb-1">Estimated Price</p>
                {quoteEquipment ? (
                  <p className="text-4xl md:text-5xl font-black text-[#FF0033]" data-testid="quote-price">
                    £{(() => {
                      const item = speakers.find(s => s.id === quoteEquipment);
                      if (!item) return '0.00';
                      const isBundle = item.is_bundle;
                      return (item.price_per_day * parseInt(quoteDays) * (isBundle ? 1 : parseInt(quoteQty))).toFixed(2);
                    })()}
                  </p>
                ) : (
                  <p className="text-4xl md:text-5xl font-black text-white/20" data-testid="quote-price">£—</p>
                )}
                <p className="text-white/40 text-xs mt-1">Delivery fee calculated on confirmation</p>
              </div>
              <Button
                onClick={() => {
                  if (quoteEquipment) navigate(`/book/${quoteEquipment}`);
                }}
                disabled={!quoteEquipment}
                className="bg-[#FF0033] text-white hover:bg-[#FF0033]/80 px-10 py-6 text-base font-bold transition-all disabled:opacity-30"
                data-testid="quote-book-btn"
              >
                Book This
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Videos Section */}
      <section id="videos" className="py-24 md:py-32 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="mb-16">
            <p className="text-xs uppercase tracking-[0.2em] text-[#FF0033] mb-4">See Us In Action</p>
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight" data-testid="videos-section-title">
              Our Sound Setups
            </h2>
            <p className="text-white/70 mt-4 max-w-2xl">
              Check out our equipment in action at real events. Quality sound that gets the party going.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Video 1 */}
            <div className="neon-card overflow-hidden group" data-testid="video-1">
              <div className="aspect-video bg-[#1E1E1E]">
                <video 
                  className="w-full h-full object-cover"
                  controls
                  playsInline
                  preload="none"
                  poster=""
                >
                  <source src="https://customer-assets.emergentagent.com/job_sound-rental-3/artifacts/mn0m9vtg_IMG_3082.MOV" type="video/mp4" />
                  <source src="https://customer-assets.emergentagent.com/job_sound-rental-3/artifacts/mn0m9vtg_IMG_3082.MOV" />
                  Your browser does not support the video tag.
                </video>
              </div>
              <div className="p-4">
                <h3 className="font-bold">Sound Setup</h3>
                <p className="text-white/70 text-sm">Live event setup</p>
              </div>
            </div>
            
            {/* Video placeholder 2 */}
            <div className="neon-card overflow-hidden group" data-testid="video-2">
              <div className="aspect-video bg-[#1E1E1E]">
                <video 
                  className="w-full h-full object-cover"
                  controls
                  playsInline
                  preload="none"
                >
                  <source src="https://customer-assets.emergentagent.com/job_sound-rental-3/artifacts/kc64rb22_IMG_1972.MOV" type="video/mp4" />
                  <source src="https://customer-assets.emergentagent.com/job_sound-rental-3/artifacts/kc64rb22_IMG_1972.MOV" />
                  Your browser does not support the video tag.
                </video>
              </div>
              <div className="p-4">
                <h3 className="font-bold">Event Setup #2</h3>
                <p className="text-white/70 text-sm">Live event setup</p>
              </div>
            </div>
            
            {/* Video placeholder 3 */}
            <div className="neon-card overflow-hidden group" data-testid="video-3">
              <div className="aspect-video bg-[#1E1E1E]">
                <video 
                  className="w-full h-full object-cover"
                  controls
                  playsInline
                  preload="none"
                >
                  <source src="https://customer-assets.emergentagent.com/job_sound-rental-3/artifacts/wvw3dupj_IMG_3556.MOV" type="video/mp4" />
                  <source src="https://customer-assets.emergentagent.com/job_sound-rental-3/artifacts/wvw3dupj_IMG_3556.MOV" />
                  Your browser does not support the video tag.
                </video>
              </div>
              <div className="p-4">
                <h3 className="font-bold">Event Setup #3</h3>
                <p className="text-white/70 text-sm">Live event setup</p>
              </div>
            </div>

            {/* Video 4 */}
            <div className="neon-card overflow-hidden group" data-testid="video-4">
              <div className="aspect-video bg-[#1E1E1E]">
                <video 
                  className="w-full h-full object-cover"
                  controls
                  playsInline
                  preload="none"
                >
                  <source src="https://customer-assets.emergentagent.com/job_641f6878-5cef-40eb-8d31-2194fc50afac/artifacts/mzkudxya_IMG_3980.MOV" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
              <div className="p-4">
                <h3 className="font-bold">Event Setup #4</h3>
                <p className="text-white/70 text-sm">Live event setup</p>
              </div>
            </div>

            {/* Video 5 */}
            <div className="neon-card overflow-hidden group" data-testid="video-5">
              <div className="aspect-video bg-[#1E1E1E]">
                <video 
                  className="w-full h-full object-cover"
                  controls
                  playsInline
                  preload="none"
                >
                  <source src="https://customer-assets.emergentagent.com/job_641f6878-5cef-40eb-8d31-2194fc50afac/artifacts/caqjeh3h_IMG_3978.mov" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
              <div className="p-4">
                <h3 className="font-bold">Event Setup #5</h3>
                <p className="text-white/70 text-sm">Live event setup</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SoundCloud Mixes Section */}
      <section id="mixes" className="py-24 md:py-32 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs uppercase tracking-[0.2em] text-[#FF0033] mb-4">DJ Kayz</p>
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight" data-testid="mixes-section-title">
              Live Mixes
            </h2>
            <p className="text-white/70 mt-4 max-w-xl mx-auto">
              Listen to my latest sets and live recordings on SoundCloud.
            </p>
          </div>

          <div className="relative">
            <div className="flex gap-6 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide" data-testid="mixes-carousel" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
              {[
                { title: "LIVE @ DISTURBING SUMMER 2026", url: "djkayzuk/live-disturbing-summer-2026", plays: "3.9K" },
                { title: "LIVE @ VELLZ TOUCH OF WHITE AFFAIR", url: "djkayzuk/live-vellz-touch-of-white-affair-hosted-by-shakks", plays: "23.7K" },
                { title: "LIVE @ DOUBLE OR NOTHING", url: "djkayzuk/live-double-or-nothing-hosted-by-shakks", plays: "16.8K" },
                { title: "LIVE @ PIRATES", url: "djkayzuk/live-pirates-16102025-hosted-by-shakks", plays: "5.8K" },
                { title: "LIVE @ GYAL AFFI MARCH", url: "djkayzuk/live-gyal-affi-march", plays: "4K" },
                { title: "DJ 3Hundred's Birthday", url: "djkayzuk/live-dj-3hundreds-birthday-new-year-fresh-cash-hosted-by-ks-the-host", plays: "3.9K" },
                { title: "IN YOUR FEELINGS", url: "djkayzuk/in-your-feelings-1", plays: "1K" },
                { title: "LIVE @ STACTOBER", url: "djkayzuk/live-stactober-hosted-by-shakks", plays: "2.5K" }
              ].map((mix, i) => (
                <div key={i} className="min-w-[320px] md:min-w-[400px] snap-start" data-testid={`mix-${i}`}>
                  <div className="neon-card overflow-hidden hover:border-[#FF0033]/40 transition-all">
                    <iframe
                      width="100%"
                      height="166"
                      scrolling="no"
                      frameBorder="no"
                      allow="autoplay"
                      src={`https://w.soundcloud.com/player/?url=https%3A//soundcloud.com/${mix.url}&color=%23FF0033&auto_play=false&hide_related=true&show_comments=false&show_user=true&show_reposts=false&show_teaser=false`}
                      title={mix.title}
                    />
                    <div className="p-4 flex items-center justify-between">
                      <div>
                        <p className="text-sm font-bold truncate max-w-[250px]">{mix.title}</p>
                        <p className="text-xs text-white/50">Hosted by Shakks</p>
                      </div>
                      <span className="text-xs text-[#FF0033] font-bold">{mix.plays} plays</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="text-center mt-10">
            <a
              href="https://soundcloud.com/djkayzuk"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-[#FF5500] text-white px-8 py-4 rounded-lg font-bold text-base hover:opacity-90 transition-opacity"
              data-testid="soundcloud-btn"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M1.175 12.225c-.051 0-.094.046-.101.1l-.233 2.154.233 2.105c.007.058.05.098.101.098.05 0 .09-.04.099-.098l.255-2.105-.27-2.154c-.009-.057-.049-.1-.099-.1zm-.899.828c-.06 0-.091.037-.104.094L0 14.479l.172 1.282c.013.06.045.094.104.094.057 0 .09-.037.099-.094l.193-1.282-.193-1.332c-.009-.06-.042-.094-.099-.094zm1.835-1.585c-.067 0-.113.056-.12.114l-.217 2.897.217 2.784c.008.06.054.113.12.113.07 0 .11-.053.12-.113l.248-2.784-.248-2.897c-.01-.058-.05-.114-.12-.114zm.924-.463c-.077 0-.129.065-.137.129l-.2 3.36.2 3.196c.008.068.06.129.137.129.076 0 .126-.06.136-.129l.228-3.196-.228-3.36c-.01-.064-.06-.129-.136-.129zm.93-.399c-.086 0-.14.073-.148.146l-.19 3.76.19 3.466c.007.074.062.146.148.146.084 0 .138-.072.148-.146l.213-3.466-.213-3.76c-.01-.073-.064-.146-.148-.146zm.955-.217c-.096 0-.155.083-.16.16l-.173 3.977.173 3.588c.005.08.064.16.16.16.094 0 .153-.08.16-.16l.195-3.588-.196-3.977c-.006-.077-.065-.16-.159-.16zm.98-.12c-.104 0-.166.09-.172.175l-.163 4.097.163 3.65c.006.087.068.175.172.175s.164-.088.172-.175l.185-3.65-.185-4.097c-.008-.085-.068-.176-.172-.176zm1.005-.064c-.114 0-.178.1-.184.19l-.153 4.161.152 3.68c.007.093.07.19.185.19.113 0 .176-.097.184-.19l.17-3.68-.17-4.161c-.008-.09-.07-.19-.184-.19zm3.882-1.306c-.166 0-.178.007-.332.019-1.03 0-2.016.398-2.78 1.096a3.506 3.506 0 0 0-.224-.014c-.122 0-.189.106-.196.206l-.14 4.218.14 3.69c.007.1.074.206.196.206.122 0 .188-.106.196-.206l.162-3.69-.162-4.218c-.008-.1-.074-.206-.196-.206.06 0 .12.01.178.03a3.5 3.5 0 0 1 2.826-1.418c.167 0 .332.012.494.037a5.476 5.476 0 0 1 4.727 5.432c0 .162-.007.322-.02.48a3.487 3.487 0 0 1 1.584-.38 3.5 3.5 0 0 1 0 7H11.89c-.114 0-.189-.106-.196-.206V9.37c.019-.138.114-.236.234-.27.416-.12.862-.184 1.312-.184 2.706 0 4.913 2.204 4.913 4.913 0 .166-.009.33-.025.492a3.487 3.487 0 0 1 1.563-.37 3.5 3.5 0 0 1 0 7h-8.36"/></svg>
              Listen on SoundCloud
            </a>
          </div>
        </div>
      </section>

      {/* Instagram Feed Section */}
      <section id="instagram" className="py-24 md:py-32 px-6 bg-[#1A1A1A]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs uppercase tracking-[0.2em] text-[#FF0033] mb-4">Follow Us</p>
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight" data-testid="instagram-section-title">
              @thehumblehires
            </h2>
            <p className="text-white/70 mt-4 max-w-xl mx-auto">
              Check out our latest setups, events, and behind the scenes on Instagram.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-10">
            {[
              { id: "ig1", gradient: "from-[#FF0033]/20 to-[#FF0033]/5", icon: "speaker", label: "Speaker Setups" },
              { id: "ig2", gradient: "from-[#FFD700]/20 to-[#FFD700]/5", icon: "party", label: "Events & Parties" },
              { id: "ig3", gradient: "from-[#FF0033]/20 to-purple-900/20", icon: "dj", label: "DJ Sets" },
              { id: "ig4", gradient: "from-[#39FF14]/20 to-[#39FF14]/5", icon: "bts", label: "Behind the Scenes" },
              { id: "ig5", gradient: "from-[#FF0033]/10 to-[#FFD700]/10", icon: "setup", label: "Full Setups" },
              { id: "ig6", gradient: "from-purple-900/20 to-[#FF0033]/10", icon: "vibe", label: "Vibes" },
              { id: "ig7", gradient: "from-[#FFD700]/10 to-[#39FF14]/10", icon: "crowd", label: "Crowd Moments" },
              { id: "ig8", gradient: "from-[#FF0033]/20 to-[#1A1A1A]", icon: "gear", label: "Our Equipment" }
            ].map(item => (
              <a
                key={item.id}
                href="https://www.instagram.com/thehumblehires/"
                target="_blank"
                rel="noopener noreferrer"
                className={`aspect-square bg-gradient-to-br ${item.gradient} border border-white/10 rounded-lg flex flex-col items-center justify-center gap-3 hover:border-[#FF0033]/40 hover:scale-[1.02] transition-all group cursor-pointer`}
                data-testid={`ig-tile-${item.id}`}
              >
                <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center group-hover:bg-[#FF0033]/10 transition-colors">
                  <svg className="w-6 h-6 text-white/50 group-hover:text-[#FF0033] transition-colors" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
                    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
                    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
                  </svg>
                </div>
                <span className="text-xs text-white/50 uppercase tracking-wider group-hover:text-white/70 transition-colors">{item.label}</span>
              </a>
            ))}
          </div>

          <div className="text-center">
            <a
              href="https://www.instagram.com/thehumblehires/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-gradient-to-r from-[#FF0033] to-[#cc0029] text-white px-8 py-4 rounded-lg font-bold text-base hover:opacity-90 transition-opacity"
              data-testid="follow-instagram-btn"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
                <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
                <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
              </svg>
              Follow @thehumblehires
            </a>
          </div>
        </div>
      </section>

      {/* Music Packs Section */}
      {musicPacks.length > 0 && (
        <section id="music" className="py-24 md:py-32 px-6 bg-[#1A1A1A]">
          <div className="max-w-7xl mx-auto">
            <div className="mb-16 text-center">
              <p className="text-xs uppercase tracking-[0.2em] text-[#FF0033] mb-4">The Humble Hires</p>
              <h2 className="text-3xl md:text-5xl font-bold tracking-tight" data-testid="music-section-title">
                DJ Pack Library
              </h2>
              <p className="text-white/70 mt-4 max-w-xl mx-auto">
                Serato Ready. Plug & Play. The only music library you'll ever need.
              </p>
            </div>

            {musicPacks.map(pack => (
              <div key={pack.id} className="max-w-5xl mx-auto neon-card overflow-hidden group hover:border-[#FF0033]/40 transition-all" data-testid={`music-pack-${pack.id}`}>
                <div className="grid grid-cols-1 md:grid-cols-2">
                  {/* Image */}
                  {pack.cover_image && (
                    <div className="aspect-square md:aspect-auto overflow-hidden">
                      <img src={pack.cover_image} alt={pack.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                  )}
                  {/* Details */}
                  <div className="p-6 md:p-8 flex flex-col justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <Music className="w-4 h-4 text-[#FF0033]" />
                        <span className="text-xs uppercase tracking-wider text-[#FF0033] font-bold">Crate Ready SSD</span>
                      </div>
                      <h3 className="text-2xl md:text-3xl font-black mb-3">{pack.name}</h3>
                      <p className="text-white/70 text-sm mb-5">{pack.description}</p>

                      {/* Specs Grid */}
                      <div className="grid grid-cols-2 gap-3 mb-5">
                        <div className="bg-white/5 border border-white/10 rounded-lg p-3">
                          <p className="text-[10px] uppercase tracking-wider text-white/50 mb-1">Total Size</p>
                          <p className="text-sm font-bold">2 TB (Terabyte)</p>
                        </div>
                        <div className="bg-white/5 border border-white/10 rounded-lg p-3">
                          <p className="text-[10px] uppercase tracking-wider text-white/50 mb-1">Quality</p>
                          <p className="text-sm font-bold">320 KBPS</p>
                        </div>
                        <div className="bg-white/5 border border-white/10 rounded-lg p-3">
                          <p className="text-[10px] uppercase tracking-wider text-white/50 mb-1">Drive Type</p>
                          <p className="text-sm font-bold">SanDisk SSD</p>
                        </div>
                        <div className="bg-white/5 border border-white/10 rounded-lg p-3">
                          <p className="text-[10px] uppercase tracking-wider text-white/50 mb-1">Era Span</p>
                          <p className="text-sm font-bold">1920 – 2025</p>
                        </div>
                      </div>

                      {/* What's Inside */}
                      <div className="mb-5">
                        <p className="text-xs uppercase tracking-wider text-[#FF0033] mb-2 font-bold">What's Inside</p>
                        <div className="space-y-1.5 text-sm text-white/70">
                          <p className="flex items-center gap-2"><span className="text-[#FF0033]">&#10003;</span> {pack.tracks?.length || 45}+ Genres</p>
                          <p className="flex items-center gap-2"><span className="text-[#FF0033]">&#10003;</span> Intros</p>
                          <p className="flex items-center gap-2"><span className="text-[#FF0033]">&#10003;</span> Clean & Dirty Versions</p>
                          <p className="flex items-center gap-2"><span className="text-[#FF0033]">&#10003;</span> Instrumentals & Acapellas</p>
                          <p className="flex items-center gap-2"><span className="text-[#FF0033]">&#10003;</span> Roadmix, Remixes & Mashups</p>
                        </div>
                      </div>

                      {/* Genre Tags */}
                      {pack.tracks?.length > 0 && (
                        <div className="mb-5">
                          <p className="text-xs uppercase tracking-wider text-white/50 mb-2 font-bold">Genre Samples</p>
                          <div className="flex flex-wrap gap-1.5">
                            {pack.tracks.slice(0, 12).map((genre, i) => (
                              <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-white/50">
                                {genre}
                              </span>
                            ))}
                            {pack.tracks.length > 12 && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#FF0033]/10 border border-[#FF0033]/20 text-[#FF0033]">+{pack.tracks.length - 12} more</span>}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Price + CTA */}
                    <div className="flex items-center justify-between pt-5 border-t border-white/10">
                      <span className="text-3xl font-black text-[#FF0033]">£{pack.price.toFixed(2)}</span>
                      <Button
                        onClick={() => { setBuyingPack(pack); setPackPurchased(false); setPackDiscount(null); setPackForm({ name: "", email: "", discount_code: "" }); }}
                        className="bg-[#FF0033] text-white hover:bg-[#FF0033]/80 font-bold px-6 h-12 text-base"
                        data-testid={`buy-pack-${pack.id}`}
                      >
                        <Download className="w-4 h-4 mr-2" />Buy Now
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Music Pack Purchase Dialog */}
      {buyingPack && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-black/80 backdrop-blur-sm" onClick={() => setBuyingPack(null)}>
          <div className="glass-panel border-[#FF0033]/20 max-w-md w-full p-8" onClick={e => e.stopPropagation()} data-testid="music-purchase-dialog">
            {!packPurchased ? (
              <>
                <div className="text-center mb-6">
                  <Music className="w-10 h-10 text-[#FF0033] mx-auto mb-3" />
                  <h3 className="text-xl font-bold">{buyingPack.name}</h3>
                  <p className="text-3xl font-black text-[#FF0033] mt-2">£{buyingPack.price.toFixed(2)}</p>
                </div>
                <form onSubmit={handlePackPurchase} className="space-y-4">
                  <div>
                    <label className="text-xs uppercase tracking-wider text-white/50 mb-1 block">Your Name *</label>
                    <Input value={packForm.name} onChange={e => setPackForm(f => ({ ...f, name: e.target.value }))} placeholder="Your name" className="bg-black/60 border-white/10 h-12" data-testid="pack-form-name" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider text-white/50 mb-1 block">Email *</label>
                    <Input type="email" value={packForm.email} onChange={e => setPackForm(f => ({ ...f, email: e.target.value }))} placeholder="your@email.com" className="bg-black/60 border-white/10 h-12" data-testid="pack-form-email" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider text-white/50 mb-1 block">Discount Code</label>
                    <Input value={packForm.discount_code} onChange={e => setPackForm(f => ({ ...f, discount_code: e.target.value.toUpperCase() }))} placeholder="Enter code (optional)" className="bg-black/60 border-white/10 h-12" data-testid="pack-form-discount" />
                  </div>
                  <div className="pt-2 space-y-3">
                    <p className="text-xs uppercase tracking-wider text-white/50 text-center">Choose Payment Method</p>
                    <Button type="submit" disabled={packSubmitting} className="w-full bg-[#FF0033] text-white hover:bg-[#FF0033]/80 font-bold h-12 text-base" data-testid="pack-submit">
                      {packSubmitting ? "Submitting..." : "Pay via Bank Transfer"}
                    </Button>
                    <Button type="button" onClick={handleStripePackPurchase} disabled={packSubmitting} className="w-full bg-[#635BFF] text-white hover:bg-[#635BFF]/80 font-bold h-12 text-base" data-testid="pack-stripe-submit">
                      {packSubmitting ? "Processing..." : "Pay with Card (Stripe)"}
                    </Button>
                  </div>
                </form>
                <button onClick={() => setBuyingPack(null)} className="w-full text-white/40 hover:text-white text-sm mt-4 transition-colors">Cancel</button>
              </>
            ) : (
              <div className="text-center" data-testid="pack-payment-info">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#FF0033]/20 flex items-center justify-center">
                  <Download className="w-8 h-8 text-[#FF0033]" />
                </div>
                <h3 className="text-xl font-bold mb-2">Order Submitted!</h3>
                <p className="text-white/70 text-sm mb-6">Complete your payment below. Once we confirm it, you'll receive the download link by email.</p>

                <div className="glass-panel p-6 text-left mb-4">
                  <h4 className="text-sm font-bold text-[#FF0033] uppercase tracking-wider mb-3">Payment Details</h4>
                  <div className="space-y-2 text-sm">
                    {packDiscount ? (
                      <>
                        <div className="flex justify-between"><span className="text-white/50">Original Price</span><span className="font-bold text-white/50 line-through">£{buyingPack.price.toFixed(2)}</span></div>
                        <div className="flex justify-between"><span className="text-white/50">Discount ({packDiscount.code})</span><span className="font-bold text-[#39FF14]">-{packDiscount.percent}%</span></div>
                        <div className="flex justify-between"><span className="text-white/50">Amount to Pay</span><span className="font-bold text-[#FF0033]">£{(buyingPack.price - packDiscount.saved).toFixed(2)}</span></div>
                      </>
                    ) : (
                      <div className="flex justify-between"><span className="text-white/50">Amount</span><span className="font-bold text-[#FF0033]">£{buyingPack.price.toFixed(2)}</span></div>
                    )}
                    <div className="flex justify-between"><span className="text-white/50">Account Name</span><span className="font-bold">Kymani Noble</span></div>
                    <div className="flex justify-between"><span className="text-white/50">Sort Code</span><span className="font-bold">11-05-03</span></div>
                    <div className="flex justify-between"><span className="text-white/50">Account No</span><span className="font-bold">17250569</span></div>
                  </div>
                </div>

                <Button onClick={() => window.print()} variant="outline" className="w-full mb-3 border-white/20 hover:border-white/40 font-bold" data-testid="print-pack-receipt">
                  <Printer className="w-4 h-4 mr-2" />Print Receipt
                </Button>
                <Button onClick={() => setBuyingPack(null)} className="w-full bg-white/10 text-white hover:bg-white/20 font-bold" data-testid="pack-close">Done</Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Features Section */}
      <section id="features" className="py-24 md:py-32 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="mb-16 text-center">
            <p className="text-xs uppercase tracking-[0.2em] text-[#FF0033] mb-4">Why Choose Us</p>
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight">
              Premium Service, Every Time
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: Volume2, title: "Pro Audio", desc: "Professional speakers deliver studio-quality sound for any venue size" },
              { icon: Truck, title: "Free Delivery", desc: "Free delivery and setup on orders over £100 within a 4-mile radius" },
              { icon: Zap, title: "Easy Setup", desc: "All equipment comes with cables and stands included" },
              { icon: Shield, title: "Fully Insured", desc: "Complete peace of mind with our insurance coverage" }
            ].map((feature, idx) => (
              <div key={idx} className="glass-panel p-8 text-center group hover:border-[#FF0033]/30 transition-colors">
                <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center bg-[#FF0033]/10 group-hover:bg-[#FF0033]/20 transition-colors">
                  <feature.icon className="w-8 h-8 text-[#FF0033]" />
                </div>
                <h3 className="text-lg font-bold mb-3">{feature.title}</h3>
                <p className="text-white/70 text-sm">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews / Testimonials Section */}
      {reviews.length > 0 && (
        <section id="reviews" className="py-24 md:py-32 px-6 bg-[#1A1A1A]">
          <div className="max-w-7xl mx-auto">
            <div className="mb-16 text-center">
              <p className="text-xs uppercase tracking-[0.2em] text-[#FFD700] mb-4">Testimonials</p>
              <h2 className="text-3xl md:text-5xl font-bold tracking-tight" data-testid="reviews-section-title">
                What Our Customers Say
              </h2>
              <p className="text-white/70 mt-4 max-w-2xl mx-auto">
                Real feedback from real events. See why people trust The Humble Hires.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {reviews.map((review, idx) => (
                <div 
                  key={review.id} 
                  className="glass-panel p-8 relative group hover:border-[#FFD700]/30 transition-all duration-300"
                  data-testid={`testimonial-${review.id}`}
                  style={{ animationDelay: `${idx * 0.1}s` }}
                >
                  <Quote className="w-8 h-8 text-[#FFD700]/20 absolute top-6 right-6" />
                  <div className="flex gap-1 mb-4">
                    {[1, 2, 3, 4, 5].map(s => (
                      <Star key={s} className={`w-4 h-4 ${s <= review.rating ? 'text-[#FFD700] fill-[#FFD700]' : 'text-white/10'}`} />
                    ))}
                  </div>
                  <p className="text-white/80 text-sm mb-6 leading-relaxed italic">"{review.comment}"</p>
                  <div className="border-t border-white/10 pt-4">
                    <p className="font-bold text-white">{review.customer_name}</p>
                    {review.event_type && (
                      <p className="text-xs text-[#FFD700]/60 mt-1">{review.event_type}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Leave a Review Button */}
            <div className="text-center mt-12">
              <Button
                onClick={() => setShowReviewForm(true)}
                className="bg-[#FFD700] text-black hover:bg-[#FFD700]/80 font-bold px-8 h-12 text-base"
                data-testid="leave-review-btn"
              >
                <Star className="w-4 h-4 mr-2" />Leave a Review
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* Review Form - also show if no reviews yet */}
      {reviews.length === 0 && (
        <section id="reviews" className="py-24 md:py-32 px-6 bg-[#1A1A1A]">
          <div className="max-w-7xl mx-auto text-center">
            <p className="text-xs uppercase tracking-[0.2em] text-[#FFD700] mb-4">Testimonials</p>
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-4">Be the First to Review</h2>
            <p className="text-white/70 mb-8">Had a great experience? Let others know!</p>
            <Button
              onClick={() => setShowReviewForm(true)}
              className="bg-[#FFD700] text-black hover:bg-[#FFD700]/80 font-bold px-8 h-12 text-base"
              data-testid="leave-first-review-btn"
            >
              <Star className="w-4 h-4 mr-2" />Leave a Review
            </Button>
          </div>
        </section>
      )}

      {/* Review Form Dialog */}
      {showReviewForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-black/80 backdrop-blur-sm" onClick={() => setShowReviewForm(false)}>
          <div className="glass-panel border-[#FFD700]/20 max-w-md w-full p-8" onClick={e => e.stopPropagation()} data-testid="review-form-dialog">
            <div className="text-center mb-6">
              <Star className="w-10 h-10 text-[#FFD700] mx-auto mb-3" />
              <h3 className="text-xl font-bold">Leave a Review</h3>
              <p className="text-white/50 text-sm mt-1">Share your experience with The Humble Hires</p>
            </div>
            <form onSubmit={handleReviewSubmit} className="space-y-4">
              <div>
                <label className="text-xs uppercase tracking-wider text-white/50 mb-1 block">Your Name *</label>
                <Input 
                  value={reviewForm.customer_name} 
                  onChange={e => setReviewForm(f => ({ ...f, customer_name: e.target.value }))} 
                  placeholder="Your name" 
                  className="bg-black/60 border-white/10 h-12" 
                  data-testid="review-form-name" 
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-white/50 mb-1 block">Event Type</label>
                <Input 
                  value={reviewForm.event_type} 
                  onChange={e => setReviewForm(f => ({ ...f, event_type: e.target.value }))} 
                  placeholder="e.g. Birthday Party, Wedding, Corporate" 
                  className="bg-black/60 border-white/10 h-12" 
                  data-testid="review-form-event" 
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-white/50 mb-2 block">Rating *</label>
                <div className="flex gap-2" data-testid="review-form-rating">
                  {[1, 2, 3, 4, 5].map(s => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setReviewForm(f => ({ ...f, rating: s }))}
                      className="p-1 transition-transform hover:scale-110"
                    >
                      <Star className={`w-8 h-8 ${s <= reviewForm.rating ? 'text-[#FFD700] fill-[#FFD700]' : 'text-white/20'}`} />
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-white/50 mb-1 block">Your Review *</label>
                <Textarea 
                  value={reviewForm.comment} 
                  onChange={e => setReviewForm(f => ({ ...f, comment: e.target.value }))} 
                  placeholder="Tell us about your experience..." 
                  className="bg-black/60 border-white/10 min-h-[100px]" 
                  data-testid="review-form-comment" 
                />
              </div>
              <Button 
                type="submit" 
                disabled={submittingReview} 
                className="w-full bg-[#FFD700] text-black hover:bg-[#FFD700]/80 font-bold h-12 text-base" 
                data-testid="review-form-submit"
              >
                {submittingReview ? "Submitting..." : "Post Review"}
              </Button>
            </form>
            <button onClick={() => setShowReviewForm(false)} className="w-full text-white/40 hover:text-white text-sm mt-4 transition-colors">Cancel</button>
          </div>
        </div>
      )}

      {/* FAQ Section */}
      <section id="faq" className="py-24 md:py-32 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.2em] text-[#FF0033] mb-4">Got Questions?</p>
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight" data-testid="faq-section-title">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="space-y-3" data-testid="faq-list">
            {[
              {
                q: "Where do you deliver?",
                a: "We deliver across the local area. Orders over £100 within a 4-mile radius get FREE delivery! For locations further out, a delivery fee will be calculated based on distance. Contact us with your postcode and we'll confirm."
              },
              {
                q: "Is setup included?",
                a: "Yes! Setup is available with every hire. We'll deliver the equipment, set it all up for you, and collect it after your event. Orders over £100 within 4 miles include free delivery and setup."
              },
              {
                q: "How do I pay?",
                a: "Payment is made by bank transfer. Once your booking is confirmed, you'll receive an email and/or WhatsApp message with the full payment details including bank account info."
              },
              {
                q: "Do I need to provide ID?",
                a: "Yes, a valid photo ID (passport or driving licence) is required when making a booking. If you're a returning customer, your ID will already be on file from your previous hire."
              },
              {
                q: "What's the cancellation policy?",
                a: "We do not offer refunds on confirmed bookings. However, you can reschedule your event date subject to admin approval and availability. Contact us as early as possible if you need to change dates."
              },
              {
                q: "What happens if equipment gets damaged?",
                a: "The client is responsible for any damage to the equipment during the hire period. If damage occurs, the client will be required to cover the cost of repair or replacement. We recommend taking care of the equipment and reporting any issues immediately."
              },
              {
                q: "How far in advance should I book?",
                a: "We recommend booking as early as possible to secure your preferred equipment and date. Popular dates like weekends and holidays fill up fast. You can check availability in our booking calendar."
              }
            ].map((faq, idx) => (
              <div 
                key={idx} 
                className="glass-panel overflow-hidden transition-all duration-300"
                data-testid={`faq-item-${idx}`}
              >
                <button
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  className="w-full flex items-center justify-between p-6 text-left"
                  data-testid={`faq-toggle-${idx}`}
                >
                  <span className="font-bold text-white pr-4">{faq.q}</span>
                  <ChevronDown className={`w-5 h-5 text-[#FF0033] shrink-0 transition-transform duration-300 ${openFaq === idx ? 'rotate-180' : ''}`} />
                </button>
                <div className={`overflow-hidden transition-all duration-300 ${openFaq === idx ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
                  <p className="px-6 pb-6 text-white/70 text-sm leading-relaxed">{faq.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 md:py-32 px-6 bg-[#1A1A1A]">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.2em] text-[#FF0033] mb-4">Get In Touch</p>
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight" data-testid="contact-section-title">
              Contact Us
            </h2>
            <p className="text-white/70 mt-4 max-w-xl mx-auto">
              Got a question? Send us a message or chat with us on WhatsApp.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            {/* Contact Form */}
            <form onSubmit={handleContactSubmit} className="glass-panel p-8 space-y-5" data-testid="contact-form">
              <div>
                <label className="text-xs uppercase tracking-wider text-white/50 mb-2 block">Name *</label>
                <Input
                  value={contactForm.name}
                  onChange={(e) => setContactForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="Your name"
                  className="bg-black/60 border-white/10 h-12"
                  data-testid="contact-name"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-white/50 mb-2 block">Email *</label>
                <Input
                  type="email"
                  value={contactForm.email}
                  onChange={(e) => setContactForm(f => ({ ...f, email: e.target.value }))}
                  placeholder="your@email.com"
                  className="bg-black/60 border-white/10 h-12"
                  data-testid="contact-email"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-white/50 mb-2 block">Phone</label>
                <Input
                  value={contactForm.phone}
                  onChange={(e) => setContactForm(f => ({ ...f, phone: e.target.value }))}
                  placeholder="07xxx xxxxxx"
                  className="bg-black/60 border-white/10 h-12"
                  data-testid="contact-phone"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-white/50 mb-2 block">Message *</label>
                <Textarea
                  value={contactForm.message}
                  onChange={(e) => setContactForm(f => ({ ...f, message: e.target.value }))}
                  placeholder="How can we help?"
                  rows={4}
                  className="bg-black/60 border-white/10 resize-none"
                  data-testid="contact-message"
                />
              </div>
              <Button
                type="submit"
                disabled={sendingContact}
                className="w-full bg-[#FF0033] text-black hover:bg-[#FF0033]/80 font-bold h-12 text-base"
                data-testid="contact-submit"
              >
                {sendingContact ? "Sending..." : <><Send className="w-4 h-4 mr-2" />Send Message</>}
              </Button>
            </form>

            {/* Contact Info */}
            <div className="flex flex-col justify-center space-y-8">
              <div className="glass-panel p-8">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 rounded-full bg-[#25D366]/20 flex items-center justify-center">
                    <MessageCircle className="w-6 h-6 text-[#25D366]" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg">WhatsApp</h3>
                    <p className="text-white/50 text-sm">Fastest way to reach us</p>
                  </div>
                </div>
                <a
                  href="https://wa.me/447543710680?text=Hi%20The%20Humble%20Hires!%20I%27d%20like%20to%20enquire%20about%20hiring%20equipment."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full text-center bg-[#25D366] hover:bg-[#25D366]/80 text-white font-bold py-3 px-6 rounded-md transition-colors"
                  data-testid="contact-whatsapp-btn"
                >
                  Chat on WhatsApp
                </a>
              </div>

              <div className="glass-panel p-8">
                <h3 className="font-bold text-lg mb-4">Quick Info</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex items-start gap-3">
                    <Truck className="w-4 h-4 text-[#FF0033] mt-0.5 shrink-0" />
                    <span className="text-white/70">Free delivery on orders over £100 within 4 miles</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <Zap className="w-4 h-4 text-[#FF0033] mt-0.5 shrink-0" />
                    <span className="text-white/70">Setup & collection included</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <Shield className="w-4 h-4 text-[#FF0033] mt-0.5 shrink-0" />
                    <span className="text-white/70">Photo ID required for all hires</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 px-6 relative overflow-hidden">
        <div className="absolute inset-0 glow-overlay" />
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-6">
            Ready to Make Some Noise?
          </h2>
          <p className="text-lg text-white/70 mb-8">
            Book your speakers today and get a free consultation on the best setup for your event.
          </p>
          <Button 
            onClick={() => document.getElementById('speakers').scrollIntoView({ behavior: 'smooth' })}
            className="btn-primary px-10 py-6 text-base"
            data-testid="cta-book-now"
          >
            Book Your Speakers
            <ChevronRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-white/10">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img src="https://static.prod-images.emergentagent.com/jobs/641f6878-5cef-40eb-8d31-2194fc50afac/images/9204c88f36c8aba1418f399ed70957b9e9b435b9671cfdca88f8cc5e5e0a2467.png" alt="The Humble Hires" className="h-20" />
          </div>
          <div className="flex items-center gap-6">
            <a href="/terms" className="text-white/50 hover:text-[#FF0033] text-sm transition-colors" data-testid="footer-terms-link">Terms & Conditions</a>
            <p className="text-white/50 text-sm">
              © {new Date().getFullYear()} DJ Kayz — The Humble Hires. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* Floating Music Player */}
      <audio ref={audioRef} src="https://customer-assets.emergentagent.com/job_641f6878-5cef-40eb-8d31-2194fc50afac/artifacts/grbtas5k_Drake%20-%20Janice%20STFU.mp3" loop preload="none" />
      <div className="fixed bottom-6 left-6 z-50" data-testid="music-player-btn">
        {/* Bass ring pulse behind button */}
        {isPlaying && (
          <>
            <div className="absolute inset-0 rounded-full bg-[#FF0033]/20 bass-ring" />
            <div className="absolute inset-0 rounded-full bg-[#FF0033]/10 bass-ring" style={{ animationDelay: '0.5s' }} />
          </>
        )}
        <button
          onClick={toggleMusic}
          className={`relative flex items-center gap-3 px-5 py-3 rounded-full shadow-lg transition-all duration-300 hover:scale-105 ${
            isPlaying 
              ? "bg-[#FF0033] bass-pulse" 
              : "bg-white/10 backdrop-blur-md border border-white/20 hover:border-[#FF0033]/40"
          }`}
          aria-label="Toggle music"
        >
          {isPlaying ? (
            <>
              <Pause className="w-5 h-5 text-white" />
              <div className="flex items-end gap-[3px] h-6">
                <div className="w-[3px] bg-white rounded-full bass-bar-1" />
                <div className="w-[3px] bg-white rounded-full bass-bar-2" />
                <div className="w-[3px] bg-white rounded-full bass-bar-3" />
                <div className="w-[3px] bg-white rounded-full bass-bar-4" />
                <div className="w-[3px] bg-white rounded-full bass-bar-5" />
                <div className="w-[3px] bg-white rounded-full bass-bar-2" />
                <div className="w-[3px] bg-white rounded-full bass-bar-4" />
                <div className="w-[3px] bg-white rounded-full bass-bar-1" />
              </div>
              <span className="text-xs text-white font-bold uppercase tracking-wider hidden sm:block">Now Playing</span>
            </>
          ) : (
            <>
              <Play className="w-5 h-5 text-white/70" />
              <span className="text-xs text-white/70 uppercase tracking-wider hidden sm:block">Play Music</span>
            </>
          )}
        </button>
      </div>

      {/* Floating WhatsApp Button */}
      <a
        href="https://wa.me/447543710680?text=Hi%20The%20Humble%20Hires!%20I%27d%20like%20to%20enquire%20about%20hiring%20equipment."
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-[#25D366] hover:bg-[#25D366]/90 rounded-full flex items-center justify-center shadow-lg shadow-[#25D366]/30 hover:scale-110 transition-all duration-300"
        data-testid="whatsapp-float-btn"
        aria-label="Chat on WhatsApp"
      >
        <MessageCircle className="w-7 h-7 text-white" />
      </a>
    </div>
  );
}
